// Port of GroundTeam/Support/Geo.swift (and `Coordinate.isValid` / `distance(to:)`). The ray casting is a line-for-line
// copy of `Geo.polygon(_:contains:)`, so a store on a territory's edge or vertex falls on the same side as on an
// iPhone: the server's rep visibility depends on `territoryId`, which both apps compute with this rule.

export interface Coordinate {
  latitude: number;
  longitude: number;
}

/**
 * `Coordinate.isValid`: `CLLocationCoordinate2DIsValid` (finite, latitude in [-90, 90], longitude in [-180, 180])
 * and not the (0, 0) placeholder a failed geocode leaves (Geo.swift:20).
 */
export function isValidCoordinate(c: Coordinate | null | undefined): c is Coordinate {
  if (!c) return false;
  const { latitude: lat, longitude: lon } = c;
  if (typeof lat !== 'number' || typeof lon !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return false;
  return !(lat === 0 && lon === 0);
}

/**
 * `Geo.polygon(_:contains:)`: even-odd ray casting along +latitude. Fewer than 3 points never contain anything. The
 * polygon is an open ring (the first point is not repeated). Points exactly on an edge or vertex are decided by the
 * half-open longitude test and the strict `<`, the same as Swift (so the south/west sides of an axis-aligned square
 * are inside and the north/east sides outside).
 */
export function polygonContains(polygon: readonly Coordinate[], p: Coordinate): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  let j = polygon.length - 1;
  for (let i = 0; i < polygon.length; i++) {
    const pi = polygon[i] as Coordinate;
    const pj = polygon[j] as Coordinate;
    if (pi.longitude > p.longitude !== pj.longitude > p.longitude) {
      const x = ((pj.latitude - pi.latitude) * (p.longitude - pi.longitude)) / (pj.longitude - pi.longitude) + pi.latitude;
      if (p.latitude < x) inside = !inside;
    }
    j = i;
  }
  return inside;
}

/** `Geo.centroid(of:)`: vertex average (labels go here); undefined for an empty polygon. */
export function centroid(polygon: readonly Coordinate[]): Coordinate | undefined {
  if (polygon.length === 0) return undefined;
  let lat = 0;
  let lon = 0;
  for (const c of polygon) {
    lat += c.latitude;
    lon += c.longitude;
  }
  return { latitude: lat / polygon.length, longitude: lon / polygon.length };
}

/** `Geo.areaSquareMeters(of:)`: shoelace on an equirectangular projection around the centroid. */
export function areaSquareMeters(polygon: readonly Coordinate[]): number {
  if (polygon.length < 3) return 0;
  const c = centroid(polygon);
  if (!c) return 0;
  const mPerDegLat = 111_320.0;
  const mPerDegLon = 111_320.0 * Math.cos((c.latitude * Math.PI) / 180);
  let sum = 0;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i] as Coordinate;
    const b = polygon[(i + 1) % polygon.length] as Coordinate;
    const ax = (a.longitude - c.longitude) * mPerDegLon;
    const ay = (a.latitude - c.latitude) * mPerDegLat;
    const bx = (b.longitude - c.longitude) * mPerDegLon;
    const by = (b.latitude - c.latitude) * mPerDegLat;
    sum += ax * by - bx * ay;
  }
  return Math.abs(sum) / 2;
}

/** Mean Earth radius (IUGG), meters. */
const EARTH_RADIUS_M = 6_371_008.8;

/**
 * Great-circle distance in meters (haversine). iOS uses `CLLocation.distance(from:)`, which models the WGS-84
 * ellipsoid; the two agree to well under 0.5 % (a few meters at visit-verification scale). The console only uses
 * this for display and sorting; it never decides `isVerified` (that uses the distance stored on the visit).
 */
export function distanceMeters(a: Coordinate, b: Coordinate): number {
  const toRad = Math.PI / 180;
  const dLat = (b.latitude - a.latitude) * toRad;
  const dLon = (b.longitude - a.longitude) * toRad;
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(a.latitude * toRad) * Math.cos(b.latitude * toRad) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

export function milesFromMeters(m: number): number {
  return m / 1609.344;
}

export function kmFromMeters(m: number): number {
  return m / 1000;
}

/** Bounding box `[west, south, east, north]` of the valid coordinates (MapLibre `fitBounds`); undefined when none. */
export function boundsOf(coords: Iterable<Coordinate | null | undefined>): [number, number, number, number] | undefined {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  let any = false;
  for (const c of coords) {
    if (!isValidCoordinate(c)) continue;
    any = true;
    west = Math.min(west, c.longitude);
    east = Math.max(east, c.longitude);
    south = Math.min(south, c.latitude);
    north = Math.max(north, c.latitude);
  }
  return any ? [west, south, east, north] : undefined;
}

/** `Geo.illinoisIndianaRegion` center: where the map starts with no stores and no location. */
export const DEFAULT_MAP_CENTER: Coordinate = { latitude: 39.74, longitude: -88.15 };
